import React from 'react';
import styled from 'styled-components';
import { hotelInfo, getHotelText } from '../config/hotelTexts';
import { useLanguage } from '../context/LanguageContext';

const PageHeader = styled.div`
  height: 40vh;
  min-height: 300px;
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/images/contact.jpg');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  margin-top: 70px; /* Pour laisser de l'espace pour le header fixe */
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  color: ${({ theme }) => theme.colors.white};
`;

const ContactSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  padding: ${({ theme }) => theme.spacing.xl};
`;

const ContactTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.8rem;
  position: relative;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ContactItem = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.md};
    min-width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ContactText = styled.div`
  p {
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }
`;

const OpeningHours = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.light};
`;

const OpeningHoursTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.3rem;
`;

const OpeningHoursText = styled.p`
  white-space: pre-line;
`;

const MapContainer = styled.div`
  height: 100%;
  min-height: 400px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  
  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ReservationSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xxl};
  padding: ${({ theme }) => theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  text-align: center;
`;

const ReservationTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.8rem;
`;

const ReservationText = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: 1.1rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const ReservationButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const ReservationButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background-color: ${({ theme, secondary }) => secondary ? 'transparent' : theme.colors.primary};
  color: ${({ theme, secondary }) => secondary ? theme.colors.primary : theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 600;
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.medium};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.sm};
  }
  
  &:hover {
    background-color: ${({ theme, secondary }) => secondary ? theme.colors.primary : theme.colors.secondary};
    border-color: ${({ theme, secondary }) => secondary ? theme.colors.primary : theme.colors.secondary};
    color: ${({ theme }) => theme.colors.white};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const Contact = () => {
  const { t, language } = useLanguage();
  
  return (
    <>
      <PageHeader>
        <PageTitle>{t('contact.pageTitle')}</PageTitle>
      </PageHeader>
      
      <ContactSection>
        <ContactContainer>
          <ContactGrid>
            <ContactInfo>
              <ContactTitle>{t('contact.info.title')}</ContactTitle>
              
              <ContactItem>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                </svg>
                <ContactText>
                  <p><strong>{t('contact.info.address')}</strong></p>
                  <p>{hotelInfo.name}</p>
                  <p>{hotelInfo.address.street}</p>
                  <p>{hotelInfo.address.city}</p>
                  <p>{hotelInfo.address.country}</p>
                </ContactText>
              </ContactItem>
              
              <ContactItem>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
                </svg>
                <ContactText>
                  <p><strong>{t('contact.info.phone')}</strong></p>
                  <p>{hotelInfo.contact.phone}</p>
                </ContactText>
              </ContactItem>
              
              <ContactItem>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555ZM0 4.697v7.104l5.803-3.558L0 4.697ZM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757Zm3.436-.586L16 11.801V4.697l-5.803 3.546Z"/>
                </svg>
                <ContactText>
                  <p><strong>{t('contact.info.email')}</strong></p>
                  <p>{hotelInfo.contact.email}</p>
                </ContactText>
              </ContactItem>
              
              <OpeningHours>
                <OpeningHoursTitle>{getHotelText(language, 'openingHoursTitle')}</OpeningHoursTitle>
                <OpeningHoursText>{getHotelText(language, 'openingHours')}</OpeningHoursText>
              </OpeningHours>
            </ContactInfo>
            
            <MapContainer>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d2626.0222647765277!2d-1.221864!3d48.83871400000001!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x480bfad0adae497b%3A0xcf7a12a981b93611!2s12%20Pl.%20de%20la%20R%C3%A9publique%2C%2050800%20Villedieu-les-Po%C3%AAles-Rouffigny!5e0!3m2!1sfr!2sfr!4v1746692753803!5m2!1sfr!2sfr"
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Carte de l'hôtel Saint-Pierre - 12 Place de la République, 50800 Villedieu-les-Poêles"
              ></iframe>
            </MapContainer>
          </ContactGrid>
          
          <ReservationSection>
            <ReservationTitle>{t('contact.reservation.title')}</ReservationTitle>
            <ReservationText>
              {t('contact.reservation.description')}
            </ReservationText>
            <ReservationButtons>
              <ReservationButton href={`tel:${hotelInfo.contact.phone.replace(/\s/g, '')}`} target="_blank" rel="noopener noreferrer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
                </svg>
                {t('common.callNow')}
              </ReservationButton>
              <ReservationButton href={`mailto:${hotelInfo.contact.email}`} secondary target="_blank" rel="noopener noreferrer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555ZM0 4.697v7.104l5.803-3.558L0 4.697ZM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757Zm3.436-.586L16 11.801V4.697l-5.803 3.546Z"/>
                </svg>
                {t('common.sendEmail')}
              </ReservationButton>
            </ReservationButtons>
          </ReservationSection>
          
          <div className="text-center mt-4">
            <p>{t('contact.location.description')}</p>
            <p>{t('contact.location.parking')}</p>
          </div>
        </ContactContainer>
      </ContactSection>
    </>
  );
};

export default Contact;
